import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styles from './CourseEditForm.module.scss';
import { Program, SelectOption } from '../../../../types';
import Button from '../../../../components/button/Button';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import ImageUploader from '../../../../components/image-uploader/ImageUploader';
import { isEmptyObject, validateIsEmpty } from '../../../../utils/validation';
import Checkbox from 'src/components/checkbox/Checkbox';
import Select from 'src/components/select/Select';
import DeleteModal from '../delete-modal/DeleteModal';
import { useHistory } from 'react-router-dom';
import { useDeleteProgramMutation, useUpdateProgramMutation, EditProgramFragmentDoc, useGetUrlLazyQuery } from '../../../../api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import { uploadFile } from '../../../../services/File';
import VideoForm from '@components/video-form/VideoForm'
import { handleUploadVideo } from '@services/Video';
import { useToast } from '@src/hooks/useToast';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../utils/logger';

type Props = {
  course: any | undefined;
  onSave: (p: EditProgram) => void;
  loading: boolean;
};

export const countryOptions = [
  {label: "Australia", value: "Australia"},
  {label: "Canada & USA", value: "Canada & USA"}
]

export type EditProgram = {
  id: string | undefined;
  name: string;
  description: string;
  grade: string;
  image?: string | null;
  isParentCourse: boolean;
  isSpecial: boolean;
  country: string;
  primary: any[];
  additional: any[];
  primarySllLessons: any[];
  coverVideo?: string | null;
}

const emptyCourse: EditProgram = {
  id: undefined,
  name: '',
  description: '',
  grade: '',
  image: '',
  isParentCourse: false,
  isSpecial: false,
  country: '',
  primary: [],
  additional: [],
  primarySllLessons: [],
  coverVideo: undefined,
};

const validationErrors = {
  name: '',
  grade: '',
};

type ValidationErrorKeys = keyof typeof validationErrors;

function CourseEditForm(props: Props) {
  const { onSave, loading, course } = props;
  const history = useHistory();
  const { successToast } = useToast();
  const { awsKeys } = useAWSKeys();

  const [program, setProgramValue] = useState<EditProgram>(emptyCourse);
  const [videoFile, setVideoFile] = useState<File | undefined>();
  const [imageFile, setImageFile] = useState<File | undefined>();
  const [errors, setErrors] = useState<Partial<Record<ValidationErrorKeys, string>>>(validationErrors);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileUploadingPercent, setFileUploadingPercent] = useState(0);
  const [deleteProgram, {loading: removeLoading}] = useDeleteProgramMutation({
    update() {
      history.push('/super/courses')
    },
    onCompleted() {
      setShowDeleteModal(false)
    },
  });

  const [updateProgram, {loading: updateLoading} ] = useUpdateProgramMutation({
    update(cache, res) {
      const d = res?.data?.updateProgram;
      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: { ...d },
          fragment: EditProgramFragmentDoc
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      alert('Something went wrong 1.');
    }
  });

  const [getUrl, { loading: urlLoading }] = useGetUrlLazyQuery({
    fetchPolicy: 'network-only',
  });

  const isAusCourse = useMemo(() => course?.country === countryOptions[0].label, [course?.country])

  const isLoading = useMemo(() => loading || removeLoading || updateLoading || urlLoading, [loading, removeLoading, updateLoading, urlLoading]);

  const isEdit = !!course?.id;

  useEffect(() => {
    if (course) {
      setProgramValue({
        ...emptyCourse,
        grade: course?.grade?.id || '',
        description: course.description ?? '',
        name: course.name,
        image: course.image,
        id: course.id,
        isParentCourse: !!course.isParentCourse,
        isSpecial: !!course.isSpecial,
        country: course.country || '',
        primary: course.primary || [],
        additional: course.additional || [],
        coverVideo: course.coverVideo,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  const handleChangeText = (key: keyof Program) => (value: TextInputValue) => {
    setProgramValue({
      ...program,
      [key]: value as string,
    });
  };

  const handleChangeImage = useCallback((image: string, file: File | undefined) => {
    if (image !== program.image) {
      setProgramValue({
        ...program,
        image,
      });
    }
    setImageFile(file);
  }, [program]);

  const handleChangeParentCourse = useCallback((v) => {
    setProgramValue({
      ...program,
      isParentCourse: v,
    })
  }, [program]);

  const handleChangeCountry = useCallback((v: SelectOption) => {
    setProgramValue({
      ...program,
      country: v.value,
    })
  }, [program]);

  const validate = useCallback(() => {
    const errors: Partial<Record<ValidationErrorKeys, string>> = {};

    if (validateIsEmpty(program.name)) {
      errors.name = 'Please add a name.';
    }

    return errors;
  }, [program.name]);

  const handleSave = useCallback(async () => {
    setErrors({});
    const errors = validate();

    if (isEmptyObject(errors)) {
      if (imageFile) {
        program.image = await uploadFile(awsKeys, imageFile, 'images');
      }
      onSave(program);
    } else {
      setErrors(errors);
    }
  }, [imageFile, onSave, program, validate]);

  const handleCancel = () => {
    history.replace('/super/courses')
  };

  const handleAddUnits = useCallback(async() => {
    let image = program.image;
      if (imageFile) {
        image = await uploadFile(awsKeys, imageFile, 'images');
      }
      await updateProgram({
        variables: {
          input: {
            id: program.id,
            name: program.name,
            image,
            description: program.description,
            grade: program.grade,
            isParentCourse: program.isParentCourse,
            country: program.country,
          },
        }
      });
      history.push({ pathname: '/super/courses/customize', state: { program } });
      successToast("Course is successfully updated!");
  }, [history, imageFile, program, updateProgram, successToast])

  const onProgress = useCallback((percent: number) => {
    setFileUploadingPercent(percent)
  }, []);

  const handleSetVideoFile = useCallback(async (file: File | undefined) => {
    if (file) {
      setVideoFile(file);
      const videoUrlUploadData = await getUrl({variables: {input: {metadata: file.name}}});
      if (videoUrlUploadData?.data?.videoUpload?.url) {
        await handleUploadVideo(file, videoUrlUploadData?.data?.videoUpload?.url, onProgress);
        const response = await updateProgram({
          variables: {
            input: {
              id: program.id,
              name: program.name,
              description: program.description,
              coverVideo: videoUrlUploadData?.data?.videoUpload?.uploadId,
            },
          }
        });
        setProgramValue({
          ...program,
          coverVideo: response?.data?.updateProgram?.coverVideo,
        });
      }
    } else {
      setVideoFile(undefined);
      setFileUploadingPercent(0);
    }
  }, [getUrl, onProgress, program, updateProgram]);

  const handleOpenModal = () => setShowDeleteModal(true);
  const handleCloseModal = () => {
    setShowDeleteModal(false)
  };
  const handleDelete = useCallback(() => {
    course && deleteProgram({
      variables: {
        input: {
          id: course.id,
        }
      },
    });
  }, [course, deleteProgram])

  const hasUnits = useMemo(() => {
    let result = false;
    if (course?.primary && course.primary.length > 0) {
      return true
    } else if (course?.additional && course.additional.length > 0) {
      return true
    } else if (course?.courseContents && course?.courseContents.length > 0) {
      return true
    }
    return result;
  }, [course?.primary, course?.additional, course?.courseContents])

  return (
    <div>
      <div className={styles.row}>
        <div className={styles.actions}>
          <div className={styles.title}>
            {isEdit ? 'Edit course' : 'New course'}
          </div>
          <div className={styles.buttons}>
            <Button className={styles.saveButton} disabled={isLoading} onClick={handleSave} leftIcon={isLoading ? <Spinner width={20} height={20} /> : undefined} size={'small'} label={'SAVE'}/>
            {!isEdit &&
            <Button className={styles.cancelButton} disabled={isLoading} onClick={handleCancel} size={'small'} label={'Cancel'}/>}
          </div>
        </div>
      </div>
      <div className={styles.row}>
        <TextInput hasError={!!errors.name} subTitle={errors.name} label={'Name'} id={'name'} placeholder={'Course name'} value={program.name!} onChange={handleChangeText('name')}/>
      </div>
      <div className={styles.row}>
        <Checkbox id={'parentCourse'} checked={program.isParentCourse} onChange={handleChangeParentCourse} label={'Parent course'} disabled={program.isParentCourse && program.isSpecial}/>
      </div>
      <div className={styles.row}>
        <Select label={'Country'} options={countryOptions} value={program.country} onChange={handleChangeCountry}/>
      </div>
      <div className={styles.row}>
        <TextInput
          textArea={true}
          label={'Description'}
          id={'description'}
          placeholder={'Description'}
          value={program.description}
          onChange={handleChangeText('description')}
        />
      </div>
      {program.isParentCourse && program.isSpecial ? '' :
        <div className={styles.row}>
          <ImageUploader url={program.image || ''} onChange={handleChangeImage}/>
        </div>
      }
      <VideoForm
        data={program.coverVideo || undefined}
        dataFile={videoFile}
        onChange={handleSetVideoFile}
        onChangeVideo={video => logger.info(video)}
        isUploading={(fileUploadingPercent > 0 && fileUploadingPercent < 100) || isLoading}
        isDisabled={isLoading}
        uploadingPercent={fileUploadingPercent}
        noSubTitle
        isCourse
      />
      <div className={styles.row}>
        <div className={styles.bottomActions}>
          <div className={styles.buttons}>
            <Button className={styles.saveButton} disabled={isLoading} onClick={handleAddUnits} leftIcon={isLoading ? <Spinner width={20} height={20} /> : undefined} size={'small'} label={hasUnits ?  isAusCourse ? 'EDIT CONTENT' : 'EDIT UNITS' : isAusCourse ? 'ADD CONTENT' : 'ADD UNITS'}/>
          </div>
        </div>
      </div>
      {isEdit &&
        <div className={styles.deleteRow}>
          <div className={styles.subTitle}>Warning</div>
          <Button className={styles.deleteButton} disabled={isLoading} onClick={handleOpenModal} size={'small'} label={'DELETE COURSE'}/>
        </div>}
      {showDeleteModal && <DeleteModal open={showDeleteModal} loading={removeLoading || urlLoading} onClose={handleCloseModal} onDelete={handleDelete}/>}
    </div>
  );
}

export default CourseEditForm;
